import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import { useAuth } from 'restaurantAdmin/context/AuthContext';
import {
  EDIT_RESERVATION_MODE_PARAM,
  OCCUPANT_ID_PARAM,
} from '../../utils/constants';
import { useFetchOccupantDetails } from './hooks/useFetchOccupantDetails';
import { useServiceSearchParams } from './hooks/useServiceSearchParams';
import { EditSingleTableReservationDrawer } from './EditSingleTableReservationDrawer';
import { ConciergeAvailabilityContextProvider } from '../concierge/ConciergeAvailabilityContext';
import { isReservation } from './apiHelpers';
import styles from './EditReservationDrawer.scss';
import typography from '~styles/typography.scss';
import { Button, ButtonVariants } from '@components/button/Button';
import { EditMultiTableReservationDrawer } from './EditMultiTableReservationDrawer';
import { Toggle, ToggleGroup } from '@base-ui/react';
import { useEffect, useState } from 'react';
import cx from 'classnames';

type ReservationEditMode = 'single table' | 'multi table';

export const EditReservationDrawer = () => {
  const { searchParams, exitReservationEditMode } = useServiceSearchParams();
  const { isManager } = useAuth();
  const reservationId = searchParams.get(OCCUPANT_ID_PARAM);

  const { occupant: reservation } = useFetchOccupantDetails({
    id: reservationId!,
    type: 'reservation',
  });

  const [editMode, setEditMode] = useState<ReservationEditMode>('single table');

  useEffect(() => {
    if (reservation && isReservation(reservation)) {
      setEditMode(
        reservation.isAvailabilityAgnostic ? 'multi table' : 'single table',
      );
    }
  }, [reservation?.id]);

  if (!reservation) return null;
  if (isReservation(reservation) === false) return null;

  return (
    <Drawer
      modal={false}
      disablePointerDismissal
      side={'right'}
      open={searchParams.has(EDIT_RESERVATION_MODE_PARAM)}
    >
      <div className={styles.container} data-testid="edit-reservation-drawer">
        <div className={styles.headerBar}>
          <h2 className={typography.h4}>Edit Reservation</h2>

          <div className={styles.actions}>
            <Button
              onClick={exitReservationEditMode}
              label="Cancel"
              variant={ButtonVariants.Tertiary}
            />
          </div>
        </div>

        <div className={styles.contentContainer}>
          {isManager && (
            <ToggleGroup
              className={cx(typography.h7m, styles.modeToggle)}
              defaultValue={[editMode]}
              value={[editMode]}
              onValueChange={([val]: ReservationEditMode[]) => {
                setEditMode(val);
              }}
            >
              <Toggle
                value={'single table'}
                disabled={editMode === 'single table'}
              >
                Normal
              </Toggle>
              <Toggle
                value={'multi table'}
                disabled={editMode === 'multi table'}
              >
                God Mode
              </Toggle>
            </ToggleGroup>
          )}

          {editMode === 'multi table' && isManager ? (
            <EditMultiTableReservationDrawer reservation={reservation} />
          ) : (
            <ConciergeAvailabilityContextProvider>
              <EditSingleTableReservationDrawer reservation={reservation} />
            </ConciergeAvailabilityContextProvider>
          )}
        </div>
      </div>
    </Drawer>
  );
};
